/**
 * placeTail + graftTail + tailBindInfluences — the T2 seam ADR-031 §4 names
 * (`kernel/tails/place-tail`): *turn a tube Zone into placed tail joints*, then
 * into real bones, then into the weights that make those bones move flesh.
 * Three functions, one pipeline, each testable without a scene:
 *
 *  1. {@link placeTail} — isolate (the ring, the cut, the grid) → tip (the
 *     farthest reachable flesh) → stations (the walk cut by the prior). Pure
 *     measurement: no rig, no names.
 *  2. {@link graftTail} — N BBSA-named bones `Tail1_graft…TailN_graft` under the
 *     pelvis, mapped to the `Tail1..TailN` slots with provenance `grafted`,
 *     and NO jointFrames (D4, ruled 2026-09-22: a tail is near-isotropic with
 *     near-zero twist — a primary/secondary frame per joint would author
 *     nothing a retarget uses). The `_graft` suffix is the hands' convention;
 *     the export door already renames it away generically.
 *  3. {@link tailBindInfluences} — ZONE-KEYED reown (D3), CONTAINED TO THE
 *     TUBE: the tail owns the zone's flesh inside its own tube envelope, its
 *     share ramping 0 → 1 across the first voxel away from the junction ring;
 *     the ring itself (every index at a ring position) stays the body's.
 *     `computeBindWeights` decides WHICH tail bone owns a vertex (geodesically,
 *     so a curled tail's loops never share weight); `bindGraftedDigits`
 *     (adapter) seats it.
 *
 * THE BONES REST IN THE PELVIS'S FRAME. With no jointFrame to honour, every
 * tail bone's bind rotation is the pelvis's own, so each bone's local rest is
 * the identity: a curl or a wag is the SAME small rotation on every bone, in
 * the body's axes, whatever the tail's shape. That is the cheapest possible
 * contract for an authored rack, World's procedural driver, and a person
 * rotating Tail3 in the pose tool — and it is stated here because nothing
 * else in the file says it.
 *
 * Depends on: tails/isolate-zone, tails/tip, tails/stations, hands/bind-weights,
 * hands/graft-digits (localBindFromParent), ir/rig-descriptor, ir/ontology/bbsa.
 * Depended on by: app/session/run-document (the replay), the tiger probes.
 */
import { KernelError } from '../ir/errors.js';
import { parseRigDescriptor, type MappingEntry, type RigBone, type RigDescriptor } from '../ir/rig-descriptor.js';
import { TAIL_SLOT_IDS } from '../ir/ontology/bbsa.js';
import type { Vec3Data } from '../math/vec3.js';
import { computeBindWeights, type BindVertex, type BoneSegment, type VertexInfluence } from '../hands/bind-weights.js';
import { localBindFromParent } from '../hands/graft-digits.js';
import { distanceToOccupied, voxelCoords, voxelPointToWorld, worldPointToVoxel } from '../hands/voxel-grid.js';
import type { EvidenceMarker } from '../hands/separation-profile.js';
import { isolateTailZone, TailRefusal, type IsolatedTailZone, type TailZoneMesh } from './isolate-zone.js';
import { findTailTip, type TailTip } from './tip.js';
import { placeTailStations, TAIL_SEGMENT_HEIGHT, type TailPrior, type TailStations } from './stations.js';
import { bundlePath } from './bundle-path.js';

/** A grouped fan's spine may always sit this far from flesh (voxels) — the floor, where a tip's slice is too thin to judge by. */
export const FAN_GROUP_MAX_AIR_VOXELS = 2;
/**
 * …and beyond the floor, this fraction of the bundle spine's LENGTH: the gap between
 * locks that part inside one hank, not the void of a peacock fan (spread tails put the
 * centre a sizeable part of the length from any flesh, ≈ 0.3). MEASURED 2026-09-26 on
 * Allen's own zones, worst joint / spine length: the kitsunes and Kitty ≤ 0.015; Penny's
 * two locks 0.058–0.071 at 5, 8, 12 and the default count (up to 3.7 cm on a 57 cm tail)
 * — and grouped with no gate her chain owns 1,984 vertices of both locks, 27 left to the
 * body. The bundle's local RADIUS was tried first and refused her: it falls to ~1 voxel
 * where the locks thin to their tips. No peacock specimen yet.
 */
export const FAN_GROUP_MAX_AIR_FRACTION = 0.1;

export interface PlaceTailInput {
  readonly mesh: TailZoneMesh;
  /** The zone's faces — the flesh a person pinned as a tail. */
  readonly faces: readonly number[];
  readonly zoneName: string;
  /** What the zone is, for refusals: the detector's shape, or `tail` as a person said. */
  readonly zoneKind: string;
  /** A declared bone count; absent = the default rule (length over mean radius). */
  readonly count?: number;
  /** Voxels along the zone's longest axis (default 64). */
  readonly resolution?: number;
  /** A person's root / tip, bind-world — the pins that correct the measurement. */
  readonly root?: Vec3Data;
  readonly tip?: Vec3Data;
  readonly prior?: TailPrior;
  /** A STUB tail (the zone's shape says so): one or two bones, not a curving chain. */
  readonly stub?: boolean;
  /** The body's height (world units) — the default count's segment floor ({@link TAIL_SEGMENT_HEIGHT} of it). */
  readonly bodyHeight?: number;
  /** A PLATE (a flat paddle, the detector's `flat`): the default count lands in [6, 8]. */
  readonly plate?: boolean;
  /**
   * 'grouped': a FAN (several tails from one root, the kitsune) is rigged as
   * ONE chain through the bundle's mass instead of refused — the stopgap until
   * multi-chain rigging. Absent = a fan refuses.
   */
  readonly fan?: 'grouped';
}

export interface PlacedTail {
  readonly zoneName: string;
  readonly isolated: IsolatedTailZone;
  readonly tip: TailTip;
  readonly stations: TailStations;
  /** count + 1 joint positions, bind-world, root → tip. Joint k starts `Tail{k+1}`; the last is the tip. */
  readonly jointsWorld: readonly Vec3Data[];
  /** Segment lengths in world units, root → tip. */
  readonly segmentLengthsWorld: readonly number[];
  readonly markers: readonly EvidenceMarker[];
}

/** Measure a tail zone into joints. Throws {@link TailRefusal} for a zone a person must change. */
export function placeTail(input: PlaceTailInput): PlacedTail {
  const isolated = isolateTailZone(input.mesh, input.faces, {
    zoneName: input.zoneName,
    ...(input.resolution !== undefined ? { resolution: input.resolution } : {}),
    ...(input.root !== undefined ? { root: input.root } : {}),
  });
  const grid = isolated.grid;
  // A fan's tips are counted on the tail side of the root cut, past a voxel
  // and a half (the swallowed-annulus zone: its rim lies ON the body plane).
  const { point, normal } = isolated.cutPlane;
  const dims = grid.dims as readonly [number, number, number];
  const beyondRoot = (v: number): boolean => {
    const [x, y, z] = voxelCoords(v, dims);
    const w = voxelPointToWorld(grid, { x, y, z });
    return (w.x - point[0]) * normal[0] + (w.y - point[1]) * normal[1] + (w.z - point[2]) * normal[2] > 1.5 * grid.voxelSize;
  };
  const tip = findTailTip(
    grid,
    isolated.rootVoxel,
    input.zoneName,
    input.tip !== undefined ? worldPointToVoxel(grid, input.tip) : undefined,
    beyondRoot,
    input.fan === 'grouped' ? 'grouped' : input.stub === true ? 'ignore' : 'refuse',
  );
  const bundle = tip.fanTips.length > 0 ? bundlePath(grid, isolated.rootVoxel, tip.fanTips, beyondRoot) : undefined;
  const stations = placeTailStations(grid, isolated.rootVoxel, tip.tip, {
    zoneName: input.zoneName,
    zoneKind: input.zoneKind,
    ...(input.count !== undefined ? { count: input.count } : {}),
    ...(input.prior !== undefined ? { prior: input.prior } : {}),
    ...(input.stub === true ? { stub: true } : {}),
    ...(input.bodyHeight !== undefined ? { minSegment: (TAIL_SEGMENT_HEIGHT * input.bodyHeight) / grid.voxelSize } : {}),
    ...(input.plate === true ? { plate: true } : {}),
    // A grouped fan walks the bundle's MASS, not one tail's flesh (bundle-path).
    ...(bundle !== undefined ? { path: bundle } : {}),
  });
  if (bundle !== undefined) {
    // The failure a grouped fan can have: its centre of mass in the AIR between
    // spread tails (a peacock fan). Measured on the joints against the bundle's
    // length (a narrow gap between locks of one hank is no peacock), refused by name.
    const air = Math.max(...stations.joints.map((j) => distanceToOccupied(grid, j)));
    if (air > Math.max(FAN_GROUP_MAX_AIR_VOXELS, FAN_GROUP_MAX_AIR_FRACTION * bundle.length)) {
      throw new TailRefusal(
        `zone "${input.zoneName}": ${tip.fanTips.length} tails spread too far to group — the bundle's centre runs ` +
          `${((air * grid.voxelSize) * 100).toFixed(1)} cm from any flesh, so one chain would hang in the air between them.`,
      );
    }
  }
  return {
    zoneName: input.zoneName,
    isolated,
    tip,
    stations,
    jointsWorld: stations.joints.map((j) => voxelPointToWorld(grid, j)),
    segmentLengthsWorld: stations.segmentLengths.map((l) => l * grid.voxelSize),
    markers: [...isolated.markers, ...tip.markers],
  };
}

/** A cannot-graft condition that is a wiring bug (the rig lacks what the Document promised). */
export class TailGraftError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(`Cannot graft a tail (${issues.length} issue(s)):\n` + issues.map((i) => `  - ${i}`).join('\n'));
  }
}

export interface GraftTailOptions {
  /** The rig bone the chain hangs from — the one mapped to `Hips`. */
  readonly pelvisBone: string;
  /** Where Tail1 pivots, bind-world, when a person placed it (`target.tails[].pivot`); absent = the placed root on the skin. */
  readonly rootAt?: Vec3Data;
}

/**
 * PLACE THE ROOT (Allen, 2026-10-05, the lemur): a placed chain pivots on the SKIN —
 * joint 0 is the junction ring's centre — and on a zone grown up toward the belt
 * that ring sits high and out from the body (3 cm off the lemur's buttock). A
 * person drags the pivot where the tail should turn; the Document keeps it
 * (`target.tails[].pivot`) and {@link graftTail} seats Tail1 there. Only the
 * pivot moves — the chain's other joints and the zone are the measurement — and
 * the bind's seam widens to the pivot's distance from joint 0
 * ({@link tailPivotSeam}) so the base blends hip → tail instead of creasing.
 */
export function tailPivotSeam(placed: PlacedTail, pivot: Vec3Data): number {
  const j0 = placed.jointsWorld[0]!;
  return Math.hypot(pivot.x - j0.x, pivot.y - j0.y, pivot.z - j0.z);
}

export interface GraftTailResult {
  readonly rig: RigDescriptor;
  readonly mappingAdditions: readonly MappingEntry[];
  /** The new rig-bone names, root → tip (`Tail1_graft`…). */
  readonly bones: readonly string[];
}

/** The rig bone mapped to `Hips`, or null — the chain's parent. */
export function tailParentBone(rig: RigDescriptor): string | null {
  return rig.mapping.find((m) => m.ontologyId === 'Hips')?.rigBone ?? null;
}

/**
 * Graft the placed chain onto the rig as real, mapped bones. Refuses (as a
 * {@link TailRefusal}, a thing a person can act on) a rig that already carries
 * a tail chain — a reopened export wears `Tail1..N` already, and a second
 * graft would stack a chain inside the first.
 */
export function graftTail(rig: RigDescriptor, placed: PlacedTail, options: GraftTailOptions): GraftTailResult {
  const pelvis = rig.bones.find((b) => b.name === options.pelvisBone);
  if (pelvis === undefined) {
    throw new TailGraftError([`pelvis bone "${options.pelvisBone}" is not in the rig — the chain has nothing to hang from`]);
  }
  const count = placed.jointsWorld.length - 1;
  const ids = TAIL_SLOT_IDS.slice(0, count);
  if (ids.length !== count) throw new TailGraftError([`${count} bones exceed the ${TAIL_SLOT_IDS.length} tail slots`]);
  const existing = new Set(rig.bones.map((b) => b.name));
  const clash = ids.filter((id) => existing.has(id) || existing.has(`${id}_graft`));
  const mapped = new Set(rig.mapping.map((m) => m.ontologyId));
  if (clash.length > 0 || ids.some((id) => mapped.has(id))) {
    throw new TailRefusal(
      `this rig already carries a tail chain (${[...clash, ...ids.filter((id) => mapped.has(id))].slice(0, 3).join(', ')}…) — ` +
        `a reopened export wears its tail; declare a tail only on the character it was cut from.`,
    );
  }

  const bones: RigBone[] = [];
  const mappingAdditions: MappingEntry[] = [];
  let parentWorld = pelvis.worldBind;
  let parentName = pelvis.name;
  for (let k = 0; k < count; k++) {
    const position = k === 0 && options.rootAt !== undefined ? options.rootAt : placed.jointsWorld[k]!;
    const worldBind = { position, rotation: pelvis.worldBind.rotation, scale: { x: 1, y: 1, z: 1 } };
    const name = `${ids[k]!}_graft`;
    bones.push({ name, parent: parentName, localBind: localBindFromParent(parentWorld, worldBind), worldBind });
    mappingAdditions.push({ rigBone: name, ontologyId: ids[k]!, confidence: 0.9, provenance: 'grafted' });
    parentWorld = worldBind;
    parentName = name;
  }
  const next = parseRigDescriptor({ ...rig, bones: [...rig.bones, ...bones], mapping: [...rig.mapping, ...mappingAdditions] });
  return { rig: next, mappingAdditions, bones: bones.map((b) => b.name) };
}

export interface TailBindResult {
  /** Tail-bone influences, each vertex's shares summing to its ramp factor f ∈ (0, 1]. */
  readonly influences: readonly VertexInfluence[];
  /** Zone vertices wholly the tail's (f = 1). */
  readonly verticesOwned: number;
  /** Zone vertices in the one-voxel seam band (0 < f < 1). */
  readonly verticesBlended: number;
  /** Zone vertices the body keeps (f = 0): the junction ring, the base behind the root plane, and the envelope's refusals. */
  readonly verticesRetained: number;
  /** Of the retained: zone vertices at a junction position (the ring, or any position a body face also uses) — held so no body face tears. */
  readonly verticesHeldAtJunction: number;
  /** Of the retained: zone vertices outside the tube envelope that the BODY claims (nearer the junction along the surface) — body flesh the chain does not pass through. */
  readonly verticesOutsideTube: number;
  /** Zone vertices the voxel field could not reach — left as painted, named. */
  readonly verticesOutsideVolume: number;
  /** Per tail bone: how many vertices it dominates — the chain-coverage evidence. */
  readonly dominance: readonly { readonly bone: string; readonly vertices: number }[];
  readonly markers: readonly EvidenceMarker[];
}

/**
 * The tube envelope: a zone vertex is the tail's only when it lies within
 * `TAIL_TUBE_ENVELOPE_K × r + one voxel` of the walked chain, r being the
 * walk's own local cross-section radius (the MEAN distance to the section's
 * voxels, so a solid round section's skin sits near 1.5 r). Measured on the
 * tiger 2026-09-23: owned zone vertices sit at d/r median 1.15, p99 2.2
 * (2 rings) / 1.3 (3 rings); the two beyond 2r + h are waist flesh at the root.
 */
export const TAIL_TUBE_ENVELOPE_K = 2;
/**
 * The hip → tail blend is this many tube radii (at the root) long. MEASURED 2026-10-05 on Allen's
 * lemur (8 bones, a 25° bend / wag of Tail1, worst edge stretch over the zone): a one-voxel seam
 * stretched ×4.6 / ×2.6 with 26 / 20 edges past 2×; his placed pivot's 2 cm seam ×2.2 / ×2.0; a seam
 * of ~3 radii (≈6 cm) ×1.6–1.8 / ×1.7–1.9 with NO edge past 2×, root on the skin or placed. Wider (7–8 cm)
 * was no better and threw isolated outliers. A curve (smoothstep) instead of the linear ramp was worse.
 */
export const TAIL_SEAM_RADII = 3;
/** The envelope's radius never exceeds this multiple of the walk's median radius (see {@link tailBindInfluences}). */
export const TAIL_RADIUS_CAP = 2;
/** Voxels of walk over which the chain's start direction is measured. */
const TAIL_START_BASELINE_VOXELS = 3;

type P3 = { readonly x: number; readonly y: number; readonly z: number };

/**
 * The zone-keyed reown (D3), contained to the tail's tube. `positions` are the
 * whole mesh's bind-world positions; `boneNames` are {@link graftTail}'s bones,
 * root → tip.
 *
 * WHY NOT THE ROOT PLANE ALONE (measured 2026-09-23, the tiger, 2 rings): the
 * ring's plane faces BACK but the tail leaves it and HANGS — its axis runs
 * nearly parallel to the plane, a few centimetres behind it. An unbounded
 * signed-distance ramp therefore left the tail's near-body side on the thigh:
 * 65 zone vertices ten-plus centimetres down the tail kept f = 0 and 36 more
 * sat part-way, so an 8° bend per bone stretched 52 tail faces up to 18.6× —
 * the "snag" Allen saw in motion and no weight view could show. So:
 *
 *  - ONE FACTOR PER POSITION. f is computed per welded position and shared by
 *    every index there. A position any non-zone vertex also uses (the junction
 *    ring and its UV twins) is the body's: f = 0, so no body face stretches
 *    and no twin tears (3 ring twins tore before: f 0.19–0.5 against 0).
 *  - THE SEAM IS THE RING. f = clamp(distance to the nearest ring position / h):
 *    one voxel of blend away from the junction, then the tail's outright.
 *  - THE ROOT PLANE IS BOUNDED. It still keeps the body side, but only at the
 *    base — flesh no more than one voxel along the chain's start direction.
 *    Beside the hanging tail, farther down, the plane says nothing.
 *  - THE TUBE ENVELOPE. A zone vertex farther from the chain than
 *    {@link TAIL_TUBE_ENVELOPE_K}·r + h keeps its paint, counted and named in
 *    the `tail-bind-contained` marker — never silent.
 */
export interface TailBindOptions {
  /**
   * The seam's width, world units: tail flesh ramps from the body's to the tail's over this
   * distance from the junction ring. Absent (or under a voxel) = one voxel. A placed pivot
   * passes {@link tailPivotSeam}, so the base bends instead of creasing at the skin.
   */
  readonly seam?: number;
}

export function tailBindInfluences(
  placed: PlacedTail,
  positions: ArrayLike<number>,
  boneNames: readonly string[],
  /** The mesh's triangles — with them, flesh outside the envelope is judged by the SURFACE it hangs from (see the header). */
  indices?: ArrayLike<number>,
  options: TailBindOptions = {},
): TailBindResult {
  const { grid, zoneVertices, cutPlane, ringVertices } = placed.isolated;
  const joints = placed.stations.joints;
  const grouped = placed.tip.fanTips.length > 0;
  if (boneNames.length !== joints.length - 1) {
    throw new TailGraftError([`${boneNames.length} bone names for ${joints.length - 1} placed segments`]);
  }
  const segments: BoneSegment[] = boneNames.map((boneId, k) => ({ boneId, from: joints[k]!, to: joints[k + 1]! }));
  const vertices: BindVertex[] = [];
  for (const v of zoneVertices) {
    vertices.push({ index: v, position: { x: positions[v * 3]!, y: positions[v * 3 + 1]!, z: positions[v * 3 + 2]! } });
  }
  const weights = computeBindWeights(grid, segments, vertices);

  const h = grid.voxelSize;
  // The seam is about THREE TUBE RADII wide at the root (TAIL_SEAM_RADII), never less than a
  // placed pivot's offset or one voxel — see the constant for the measurement.
  const rootRadius = (placed.stations.radii[0] ?? 0) * h;
  const seam = Math.max(h, options.seam ?? 0, TAIL_SEAM_RADII * rootRadius);
  const at = (v: number): P3 => ({ x: positions[v * 3]!, y: positions[v * 3 + 1]!, z: positions[v * 3 + 2]! });
  const key = (v: number): string => `${positions[v * 3]},${positions[v * 3 + 1]},${positions[v * 3 + 2]}`;

  // --- positions: which the zone owns alone, which a body vertex shares -------
  const zoneSet = new Set<number>(zoneVertices);
  const zoneKeys = new Map<string, number>(); // position → a representative index
  for (const v of zoneVertices) if (!zoneKeys.has(key(v))) zoneKeys.set(key(v), v);
  const junctionKeys = new Set<string>();
  for (const v of ringVertices) junctionKeys.add(key(v));
  const vertexCount = Math.floor(positions.length / 3);
  for (let v = 0; v < vertexCount; v++) {
    if (zoneSet.has(v)) continue;
    const k = key(v);
    if (zoneKeys.has(k)) junctionKeys.add(k);
  }
  const ringPoints: P3[] = [];
  const seenRing = new Set<string>();
  for (const v of ringVertices) {
    const k = key(v);
    if (seenRing.has(k)) continue;
    seenRing.add(k);
    ringPoints.push(at(v));
  }

  // --- the walked chain, bind-world, with its local radius --------------------
  // The radius is capped at TAIL_RADIUS_CAP × the walk's median: the first
  // station's cross-section also cuts the root's cap fan, and a wide zone's cap
  // swells it (tube-on-slab with its annulus pinned: 0.22 against a 0.05 tube).
  const chain = placed.stations.path.stations.map((s) => ({ p: voxelPointToWorld(grid, s.at), r: s.radius * h }));
  const sortedR = chain.map((c) => c.r).sort((a, b) => a - b);
  const rCap = TAIL_RADIUS_CAP * (sortedR[Math.floor(sortedR.length / 2)] ?? 0);
  const nearestOnChain = (p: P3): { d: number; r: number } => {
    const c0 = chain[0]!;
    let best = { d: Math.hypot(p.x - c0.p.x, p.y - c0.p.y, p.z - c0.p.z), r: c0.r };
    for (let k = 0; k + 1 < chain.length; k++) {
      const a = chain[k]!;
      const b = chain[k + 1]!;
      const abx = b.p.x - a.p.x;
      const aby = b.p.y - a.p.y;
      const abz = b.p.z - a.p.z;
      const l2 = abx * abx + aby * aby + abz * abz;
      const t = l2 > 0 ? Math.min(1, Math.max(0, ((p.x - a.p.x) * abx + (p.y - a.p.y) * aby + (p.z - a.p.z) * abz) / l2)) : 0;
      const d = Math.hypot(p.x - a.p.x - t * abx, p.y - a.p.y - t * aby, p.z - a.p.z - t * abz);
      if (d < best.d) best = { d, r: a.r * (1 - t) + b.r * t };
    }
    return { d: best.d, r: Math.min(best.r, rCap) };
  };
  // The chain's start direction over a baseline of a few voxels — a single
  // station step wobbles by a fraction of a voxel, which flips the sign of a
  // projection for flesh far to the side.
  const c0 = chain[0]!.p;
  const far = placed.stations.path.stations.find((s) => s.arclength >= TAIL_START_BASELINE_VOXELS) ?? placed.stations.path.stations[placed.stations.path.stations.length - 1]!;
  const cf = voxelPointToWorld(grid, far.at);
  const tl = Math.hypot(cf.x - c0.x, cf.y - c0.y, cf.z - c0.z);
  const tangent = tl > 0 ? { x: (cf.x - c0.x) / tl, y: (cf.y - c0.y) / tl, z: (cf.z - c0.z) / tl } : null;
  /** At the base: no farther along the chain's start direction than one voxel — the plane's own region. */
  const atBase = (p: P3): boolean => tangent === null || (p.x - c0.x) * tangent.x + (p.y - c0.y) * tangent.y + (p.z - c0.z) * tangent.z <= h;

  // --- one factor per position ------------------------------------------------
  const [px, py, pz] = cutPlane.point;
  const [nx, ny, nz] = cutPlane.normal;
  const factorAt = new Map<string, number>();
  const reason = new Map<string, 'junction' | 'outside'>();
  for (const [k, v] of zoneKeys) {
    if (junctionKeys.has(k)) {
      factorAt.set(k, 0);
      reason.set(k, 'junction');
      continue;
    }
    const p = at(v);
    const near = nearestOnChain(p);
    // A GROUPED fan: the person called the whole zone ONE tail, so the tube
    // envelope (sized for one tail round its own walk) would strand the outer
    // tails on the pelvis. The junction seam still holds.
    if (!grouped && near.d > TAIL_TUBE_ENVELOPE_K * near.r + h) {
      // Judged below, once the tube's own flesh is known: fur, or body?
      factorAt.set(k, 0);
      reason.set(k, 'outside');
      continue;
    }
    let dRing = Infinity;
    for (const q of ringPoints) dRing = Math.min(dRing, Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z));
    let f = ringPoints.length > 0 ? Math.min(1, dRing / seam) : 1;
    // (A grouped fan's outer tails leave the root nearly ALONG the plane — the
    // kitsune, 147° across — so the ring alone is its seam, as it is down the tail.)
    if (!grouped && atBase(p)) {
      const s = ((p.x - px) * nx + (p.y - py) * ny + (p.z - pz) * nz) / h;
      f = Math.min(f, Math.max(0, s));
    }
    factorAt.set(k, f);
  }

  // --- flesh outside the envelope: the TAIL's fur, or the BODY's? -------------
  // (Allen's Hamish, 2026-09-24: "several wild points aren't moving with the
  // tail" — the wolf's brush throws fur spikes far past 2r, and the envelope,
  // meant to keep BODY flesh out, kept the tail's own fur on the thigh: 500
  // zone vertices.) Along the zone's SURFACE, whichever is nearer — the tube's
  // own flesh or the junction — claims it; an island the surface never reaches
  // (a detached strand) goes by straight-line distance to the same two. Body
  // wins ties and everything at the base; the tail's claim gets the same seam
  // ramp from the ring as any tail flesh.
  const outsideKeys = [...reason.entries()].filter(([, r]) => r === 'outside').map(([k]) => k);
  let furClaimed = 0;
  if (outsideKeys.length > 0) {
    const coreKeys = [...factorAt.entries()].filter(([k, f]) => f > 0 && reason.get(k) === undefined).map(([k]) => k);
    const distTail = new Map<string, number>();
    const distBody = new Map<string, number>();
    if (indices !== undefined) {
      const adj = new Map<string, Set<string>>();
      const n = Math.floor(indices.length / 3);
      for (let f = 0; f < n; f++) {
        const tri = [indices[f * 3]!, indices[f * 3 + 1]!, indices[f * 3 + 2]!];
        if (!tri.every((v) => zoneSet.has(v))) continue;
        const ks = tri.map(key);
        for (let a = 0; a < 3; a++) {
          for (let b = 0; b < 3; b++) {
            if (a === b) continue;
            let set = adj.get(ks[a]!);
            if (set === undefined) {
              set = new Set();
              adj.set(ks[a]!, set);
            }
            set.add(ks[b]!);
          }
        }
      }
      const posOf = (k: string): P3 => at(zoneKeys.get(k)!);
      const sweep = (sources: readonly string[], into: Map<string, number>): void => {
        // Dijkstra with a sorted frontier — zones are thousands of positions.
        const heap: [number, string][] = sources.map((k) => [0, k]);
        for (const k of sources) into.set(k, 0);
        while (heap.length > 0) {
          let bi = 0;
          for (let i = 1; i < heap.length; i++) if (heap[i]![0] < heap[bi]![0]) bi = i;
          const [d, u] = heap[bi]!;
          heap[bi] = heap[heap.length - 1]!;
          heap.pop();
          if (d > (into.get(u) ?? Infinity)) continue;
          const pu = posOf(u);
          for (const w of adj.get(u) ?? []) {
            const pw = posOf(w);
            const nd = d + Math.hypot(pu.x - pw.x, pu.y - pw.y, pu.z - pw.z);
            if (nd < (into.get(w) ?? Infinity)) {
              into.set(w, nd);
              heap.push([nd, w]);
            }
          }
        }
      };
      sweep(coreKeys, distTail);
      sweep([...junctionKeys].filter((k) => zoneKeys.has(k)), distBody);
    }
    const corePoints = coreKeys.map((k) => at(zoneKeys.get(k)!));
    for (const k of outsideKeys) {
      const p = at(zoneKeys.get(k)!);
      let dt = distTail.get(k);
      let db = distBody.get(k);
      if (dt === undefined && db === undefined) {
        // An island: by straight line.
        dt = Infinity;
        for (const q of corePoints) dt = Math.min(dt, Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z));
        db = Infinity;
        for (const q of ringPoints) db = Math.min(db, Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z));
      }
      // At the base AND on the body's side of the root plane is the body's,
      // whatever the surface says (the plane's own region, as for tube flesh).
      const side = ((p.x - px) * nx + (p.y - py) * ny + (p.z - pz) * nz) / h;
      if ((dt ?? Infinity) >= (db ?? Infinity) || (atBase(p) && side <= 1)) continue;
      let dRing = Infinity;
      for (const q of ringPoints) dRing = Math.min(dRing, Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z));
      factorAt.set(k, ringPoints.length > 0 ? Math.min(1, dRing / seam) : 1);
      reason.delete(k);
      furClaimed++;
    }
  }

  let retained = 0;
  let blended = 0;
  let owned = 0;
  let heldAtJunction = 0;
  let outsideTube = 0;
  const factor = new Map<number, number>();
  for (const v of zoneVertices) {
    const k = key(v);
    const f = factorAt.get(k) ?? 0;
    factor.set(v, f);
    if (f <= 0) {
      retained++;
      if (reason.get(k) === 'junction') heldAtJunction++;
      else if (reason.get(k) === 'outside') outsideTube++;
    } else if (f < 1) blended++;
    else owned++;
  }

  // Each vertex's tail shares renormalised to EXACTLY its factor: the tail owns
  // the zone, so the bleed floor's and the four-slot cut's crumbs must not
  // leave a thigh holding 2% of the tail tip.
  const byVertex = new Map<number, VertexInfluence[]>();
  for (const inf of weights.influences) {
    const list = byVertex.get(inf.vertexIndex) ?? [];
    list.push(inf);
    byVertex.set(inf.vertexIndex, list);
  }
  // Tail flesh the voxel field never reached (a fur strand thinner than a
  // voxel) takes its nearest chain segment outright — never the thigh.
  const segWorld = segments.map((sg) => ({ boneId: sg.boneId, a: voxelPointToWorld(grid, sg.from), b: voxelPointToWorld(grid, sg.to) }));
  let nearestFilled = 0;
  for (const v of zoneVertices) {
    if ((factor.get(v) ?? 0) <= 0 || byVertex.has(v)) continue;
    const p = at(v);
    let best = segWorld[0]!;
    let bd = Infinity;
    for (const sg of segWorld) {
      const abx = sg.b.x - sg.a.x;
      const aby = sg.b.y - sg.a.y;
      const abz = sg.b.z - sg.a.z;
      const l2 = abx * abx + aby * aby + abz * abz;
      const t = l2 > 0 ? Math.min(1, Math.max(0, ((p.x - sg.a.x) * abx + (p.y - sg.a.y) * aby + (p.z - sg.a.z) * abz) / l2)) : 0;
      const d = Math.hypot(p.x - sg.a.x - t * abx, p.y - sg.a.y - t * aby, p.z - sg.a.z - t * abz);
      if (d < bd) {
        bd = d;
        best = sg;
      }
    }
    byVertex.set(v, [{ vertexIndex: v, boneId: best.boneId, weight: 1 }]);
    nearestFilled++;
  }
  const influences: VertexInfluence[] = [];
  const dominance = new Map<string, number>(boneNames.map((b) => [b, 0]));
  for (const [v, list] of byVertex) {
    const f = factor.get(v) ?? 0;
    const sum = list.reduce((s, i) => s + i.weight, 0);
    if (f <= 0 || !(sum > 0)) continue;
    let best = list[0]!;
    for (const inf of list) {
      if (inf.weight > best.weight) best = inf;
      influences.push({ vertexIndex: v, boneId: inf.boneId, weight: (inf.weight / sum) * f });
    }
    dominance.set(best.boneId, (dominance.get(best.boneId) ?? 0) + 1);
  }
  return {
    influences,
    verticesOwned: owned,
    verticesBlended: blended,
    verticesRetained: retained,
    verticesHeldAtJunction: heldAtJunction,
    verticesOutsideTube: outsideTube,
    verticesOutsideVolume: weights.verticesOutsideVolume,
    dominance: boneNames.map((bone) => ({ bone, vertices: dominance.get(bone) ?? 0 })),
    markers: [
      ...weights.markers,
      {
        code: 'tail-bind-contained',
        detail: {
          zone: placed.zoneName,
          zoneVertices: zoneVertices.length,
          heldAtJunction,
          outsideTube,
          furClaimed,
          nearestFilled,
          envelope: `${TAIL_TUBE_ENVELOPE_K}r+1voxel`,
        },
      },
    ],
  };
}

/** The mesh's vertical extent (world units) — what a tail's default count scales its shortest segment by. */
export function meshHeight(positions: ArrayLike<number>): number {
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 1; i < positions.length; i += 3) {
    const y = positions[i]!;
    if (y < lo) lo = y;
    if (y > hi) hi = y;
  }
  return hi > lo ? hi - lo : 0;
}
